import { useContext, useMemo, useState } from 'react';

import EADialog from 'components/common/EADialog';
import useStyles from 'style/js-style/components/thirdparties/ecovadis/EcovadisStyle';

import { Context } from 'states/Store';

import i18next from 'i18next';
import { isEmpty } from 'lodash';
import { FormProvider, useForm, useWatch } from 'react-hook-form';
import { SUCCESS } from 'services/common/Constants';
import {
  EcovadisEvaluationStep,
  getEcovadisThirdpartyIntegrationID,
  handleEcovadisApiCallError
} from 'services/common/EcovadisUtils';
import {
  fetchExistingEvaluation,
  sendEvRequest,
} from 'services/edge/EcovadisService';
import EcovadisFoundSupplier from '../EcovadisFoundSupplier/EcovadisFoundSupplier';
import EcovadisEvaluationBuyerStep from './EcovadisEvaluationBuyerStep';
import EcovadisEvaluationIntegrationIdStep from './EcovadisEvaluationIntegrationIdStep';
import EcovadisEvaluationSupplierStep from './EcovadisEvaluationSupplierStep';
import { isValidPhoneNumber } from 'libphonenumber-js/max';

const isPhoneValid = (phoneNumber) => {
  if(!phoneNumber) return false;
  const trimmedPhone = phoneNumber.startsWith('+')? phoneNumber.substring(1) : phoneNumber;
  return isValidPhoneNumber('+' + trimmedPhone)
}

function EcovadisEvaluationDialog({
  isOpen,
  onClose,
  initialEVData,
  initialStep = EcovadisEvaluationStep.INTEGRATION_ID,
  onValidateSuccess = () => {},
}) {
  const { classes } = useStyles();
  const [state, dispatch] = useContext(Context);
  const [currentStep, setCurrentStep] = useState(initialStep);
  const [concernedSupplier, setConcernedSupplier] = useState(initialEVData);
  const [isValidationLoading, setIsValidationLoading] = useState(false);

  const defaultValues = useMemo(() => ({
    integration_id: initialEVData?.main_integration_id || '',
    evaluationTypeRadio: initialEVData ? 'new' : 'exists', // 2 options [ 'exists', 'new']
    supplier_contact_first_name: initialEVData?.supplier_contact_first_name || '',
    supplier_contact_last_name: initialEVData?.supplier_contact_last_name || '',
    supplier_contact_email: initialEVData?.supplier_contact_email || '',
    supplier_contact_phone: isPhoneValid(initialEVData?.supplier_contact_phone) ? initialEVData?.supplier_contact_phone : '',
    buyer_contact_first_name: initialEVData?.buyer_contact_first_name || '',
    buyer_contact_last_name: initialEVData?.buyer_contact_last_name || '',
    buyer_contact_email: initialEVData?.buyer_contact_email || '',
  }), [initialEVData]);

  const formMethods = useForm({
    mode: 'onChange',
    delayError: 500,
    defaultValues,
  });

  const {
    control,
    formState: { errors },
    clearErrors,
  } = formMethods;

  const evaluationTypeRadioValue = useWatch({ control, name: 'evaluationTypeRadio' });

  const submitForm = async (data) => {
    if (isEmpty(errors)) {
      setIsValidationLoading(true);
      if (data.evaluationTypeRadio === 'exists') {
        await fetchAndSaveExistingEcovadisEvaluation(data);
      } else {
        await startEcovadisEvaluation(data);
      }
      setIsValidationLoading(false);
    }
  };

  const startEcovadisEvaluation = async (data) => {
    try {
      data = {
        ...data,
        supplier_contact_phone: '+' + data.supplier_contact_phone,
      };
      await sendEvRequest(
        state.account.id,
        state.thirdparty.thirdpartyId,
        data
      );
      dispatch({
        type: 'ALERT',
        alert: {
          type: SUCCESS,
          msg: i18next.t('ecovadis.requestForm.success', {
            email: data.supplier_contact_email,
          }),
        },
      });
      onValidateSuccess();
    } catch (error) {
      handleEcovadisApiCallError(error, state.thirdparty.thirdpartyId, dispatch);
    }
  };

  const fetchAndSaveExistingEcovadisEvaluation = async (data) => {
    try {
      await fetchExistingEvaluation(
        state.account.id,
        state.thirdparty.thirdpartyId,
        data.integration_id,
        dispatch
      );
      dispatch({
        type: 'ALERT',
        alert: {
          type: SUCCESS,
          msg: i18next.t('ecovadis.requestForm.fetchSuccess'),
        },
      });
      onValidateSuccess();
    } catch (error) {
      handleEcovadisApiCallError(error, state.thirdparty.thirdpartyId, dispatch);
    }
  };

  const getDialogTitle = () => {
    return (
      <>
        <span>{i18next.t('ecovadis.requestForm.title')} </span>
        <span className={classes.thirdpartyName}>{state.thirdparty?.name}</span>
        <div className={classes.progressBar}></div>
      </>
    );
  };

  const fetchExistingEcovadisOrGoToSupplierForm = () => {
    if (evaluationTypeRadioValue === 'exists') {
      formMethods.handleSubmit(submitForm)();
    } else {
      setCurrentStep(EcovadisEvaluationStep.SUPPLIER_FORM);
    }
  };

  return (
    <EADialog
      isOpen={isOpen}
      onCancel={() => {
        onClose();
      }}
      hasActions={false}
      maxWidth="md"
      fullWidth
      title={getDialogTitle()}
    >
      <div
        className={
          [EcovadisEvaluationStep.INTEGRATION_ID].includes(currentStep)
            ? classes.root
            : classes.rootDeclaration
        }
      >
        <FormProvider {...formMethods}>
          <form id="ecovadis-evaluation-dialog-id" onSubmit={formMethods.handleSubmit(submitForm)}>
            {[EcovadisEvaluationStep.INTEGRATION_ID].includes(currentStep) && (
              <EcovadisEvaluationIntegrationIdStep
                onCancel={() => {
                  onClose();
                }}
                onValidate={(supplier) => {
                  setConcernedSupplier(supplier);
                  if (supplier) {
                    setCurrentStep(EcovadisEvaluationStep.CONCERNED_SUPPLIER);
                  } else {
                    fetchExistingEcovadisOrGoToSupplierForm();
                  }
                }}
              />
            )}

            {[EcovadisEvaluationStep.CONCERNED_SUPPLIER].includes(
              currentStep
            ) && (
              <EcovadisFoundSupplier
                supplier={concernedSupplier}
                messageType={evaluationTypeRadioValue}
                integrationId={getEcovadisThirdpartyIntegrationID(state.thirdparty.thirdpartyId)}
                onCancel={() => {
                  setCurrentStep(EcovadisEvaluationStep.INTEGRATION_ID);
                  clearErrors();
                }}
                onValidate={fetchExistingEcovadisOrGoToSupplierForm}
                isValidationLoading={isValidationLoading}
              />
            )}

            {[EcovadisEvaluationStep.SUPPLIER_FORM].includes(currentStep) && (
              <EcovadisEvaluationSupplierStep
                onClose={onClose}
                onCancel={() => {
                  if (initialStep === EcovadisEvaluationStep.SUPPLIER_FORM) {
                    onClose();
                  } else {
                    setCurrentStep(EcovadisEvaluationStep.INTEGRATION_ID);
                    clearErrors();
                  }
                }}
                onValidate={() => {
                  setCurrentStep(EcovadisEvaluationStep.BUYER_FORM);
                }}
              />
            )}

            {[EcovadisEvaluationStep.BUYER_FORM].includes(currentStep) && (
              <EcovadisEvaluationBuyerStep
                onClose={onClose}
                onCancel={() => {
                  setCurrentStep(EcovadisEvaluationStep.SUPPLIER_FORM);
                  clearErrors();
                }}
                onValidate={formMethods.handleSubmit(submitForm)}
                isValidationLoading={isValidationLoading}
              />
            )}
          </form>
        </FormProvider>
      </div>
    </EADialog>
  );
}

export default EcovadisEvaluationDialog;
